import React, { useMemo } from "react";
import { useAppSelector } from "../../redux/configureStore";
import { format, parseISO } from "date-fns";
import SessionPresenterCard from "./SessionPresenterCard";
import StartSessionButton from "./presenting/StartSessionButton";
import { getPresenters } from "./selectors";
import useSessionId from "./useSessionId";
import { useSession } from "../api/helpers";
import { useIsPresenting } from "../api/selectors";
import GradientCard from "../common/GradientCard";
import LightTheme from "../theme/LightTheme";

interface Props {
  onEndSession: () => void;
}

const SessionDescription = ({ onEndSession }: Props) => {
  const session = useSession();
  const sessionId = useSessionId();
  const presenters = useAppSelector(getPresenters(sessionId));
  const isPresenting = useIsPresenting();

  const tempPresenters = useMemo(() => {
    const presenterIds = (session?.presenters ?? []).map(
      (presenter) => presenter.profileId,
    );
    return presenters.filter(
      (presenter) => !presenterIds.includes(presenter.id),
    );
  }, [presenters, session?.presenters]);

  if (session === undefined) {
    return null;
  }

  return (
    <div className="wrapper">
      <GradientCard>
        <div className="container">
          <div className="descriptionHeader">
            <div className="descriptionBlockHeader">
              <div className="descriptionTitleBlock">
                <div className="sessionDateWrapper">
                  <span className="sessionTime">
                    {format(parseISO(session.startTime as string), "h:mmaaa")} -{" "}
                    {format(parseISO(session.endTime as string), "h:mmaaa")}
                  </span>
                  {format(
                    parseISO(session.startTime as string),
                    "eeee, MMMM do",
                  )}
                </div>
              </div>
            </div>
            <div>
              {isPresenting && (
                <StartSessionButton onEndSession={onEndSession} />
              )}
            </div>
          </div>
          <div className="descriptionBlock">
            <h2 className="descriptionBlockTitle">{session.title}</h2>
            <p className="sessionDescriptionBlock">{session.description}</p>
          </div>
          <div className="presenters">
            <p className="presenterListHeader">Presenters</p>
            <div className="presenterList">
              {session.presenters.map((item) => (
                <SessionPresenterCard
                  key={item.profileId}
                  avatarUrl={item.avatarUrl}
                  name={item.name}
                  title={item.title}
                />
              ))}
            </div>
            {tempPresenters.length > 0 && (
              <>
                <p className="presenterListHeader">Temporary Presenters</p>
                <div className="presenterList">
                  {tempPresenters.map((item) => (
                    <SessionPresenterCard
                      key={item.id}
                      avatarUrl={item.avatarUrl}
                      name={item.name}
                      title={item.title}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </GradientCard>
      <style jsx>
        {`
          .wrapper {
            padding: 0 ${LightTheme.mainContentPadding}px;
          }
          .descriptionHeader {
            display: flex;
            justify-content: space-between;
          }
          .presenterListHeader {
            font-size: 1.2rem;
            font-weight: bold;
            color: #3a3a3b;
            padding: 0 0 10px 0;
          }
          .sessionDescriptionBlock {
            white-space: pre-line;
            font-size: 0.9rem;
          }
          .presenterList {
            display: flex;
            flex-wrap: wrap;
          }
          .descriptionTitleBlock {
            display: flex;
            align-content: center;
            align-items: center;
          }
          .descriptionBlockHeader {
            display: flex;
            justify-content: space-between;
          }
          .descriptionBlockTitle {
            font-weight: bold;
            font-size: 1.5rem;
            color: #343434;
          }
          .sessionDateWrapper {
            font-size: 0.8rem;
            color: ${LightTheme.font.secondary.color};
            display: flex;
          }
          .descriptionBlock {
            flex-direction: column;
            padding: -5px 0 0 0;
          }
          .sessionTime {
            padding: 0 20px 0 0;
            font-weight: bold;
            font-size: 0.8rem;
            align-self: center;
            color: #3a3a3b;
          }
          .container {
            padding: 20px;
          }
        `}
      </style>
    </div>
  );
};

export default SessionDescription;
